import { useTranslation } from "react-i18next";

import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import { SCHEDULES_LIMITS } from "../../../../../../Pages/Vehicles/types";
import { USER_PERMISSIONS } from "../../../../../../roles/types/enums";
import {
  DRIVERS_ACTIONS,
  ROUTES,
  VEHICLES_ACTIONS
} from "../../../../../../shared";
import { BANNER_MESSAGE_STATUSES } from "../../../../../BannerMessage/types";
import {
  DETAILS_TITLES,
  DetailsDataType,
  DetailsDto,
  ScheduleDto
} from "../../../../types";
import AccordionDetailsCard from "../AccordionDetailsCard/AccordionDetailsCard";
import NoCards from "../NoCards/NoCards";
import ScheduleQuickDownload from "../ScheduleQuickDownload/ScheduleQuickDownload";
import DriverScheduleDetailsSingleCard from "./DriverScheduleDetailsSingleCard/DriverScheduleDetailsSingleCard";
import VehicleScheduleDetailsSingleCard from "./VehicleScheduleDetailsSingleCard/VehicleScheduleDetailsSingleCard";

export interface ResourceScheduleCardsProps {
  detailsData: DetailsDataType;
  pageLocation: ROUTES;
  isLoading: boolean;
}

const ResourceScheduleCards = ({
  detailsData,
  pageLocation,
  isLoading
}: ResourceScheduleCardsProps) => {
  const { getAccessStatus } = useAuth();

  const { t } = useTranslation();

  const renderListOfSchedules = (detailsData: DetailsDto) => {
    const schedulesFromDetails = detailsData?.schedules;

    const scheduleData: { [key: string]: any } = {
      [ROUTES.Vehicles]: {
        noCardsText: "no vehicle schedule",
        noCardsTestId: "vehicle-details-no-schedule",
        resourceAction: VEHICLES_ACTIONS.QuickScheduleDownload,
        singleCardComponent: VehicleScheduleDetailsSingleCard
      },
      [ROUTES.Drivers]: {
        noCardsText: "no driver schedule",
        noCardsTestId: "driver-details-no-schedule",
        resourceAction: DRIVERS_ACTIONS.QuickScheduleDownload,
        singleCardComponent: DriverScheduleDetailsSingleCard
      }
    };

    const {
      noCardsText,
      noCardsTestId,
      resourceAction,
      singleCardComponent: SingleCardComponent
    } = scheduleData[pageLocation];

    const canManageQuickSchedules = getAccessStatus(
      USER_PERMISSIONS.ManageQuickSchedules
    );

    const canManageSchedules = getAccessStatus(
      USER_PERMISSIONS.ManageSchedules
    );

    const canManageScheduleByName = (isQuick: boolean) =>
      isQuick ? canManageQuickSchedules : canManageSchedules;

    const renderQuickDownload = () =>
      canManageQuickSchedules && (
        <ScheduleQuickDownload
          route={pageLocation}
          resourceAction={resourceAction}
          detailsData={detailsData}
        />
      );

    if (schedulesFromDetails?.length === 0) {
      return (
        <NoCards
          route={pageLocation}
          detailsData={detailsData}
          status={BANNER_MESSAGE_STATUSES.Warning}
          messageText={noCardsText}
          testId={noCardsTestId}
          quickAction={renderQuickDownload()}
        />
      );
    }

    return (
      <AccordionDetailsCard
        route={pageLocation}
        cardTitle={t(`Details##${DETAILS_TITLES.Schedules}`)}
        maxCount={SCHEDULES_LIMITS.MaxScheduleCount}
        currentCount={detailsData.schedulesCount}
        quickTitleAction={renderQuickDownload()}
      >
        {schedulesFromDetails?.map((schedule: ScheduleDto, index: number) => {
          return (
            <SingleCardComponent
              route={pageLocation}
              key={schedule.scheduleId}
              schedule={schedule}
              index={index}
              totalCount={schedulesFromDetails.length}
              canManageSchedules={canManageScheduleByName(schedule.quick)}
            />
          );
        })}
      </AccordionDetailsCard>
    );
  };

  return isLoading ? (
    <></>
  ) : (
    <>{detailsData && renderListOfSchedules(detailsData)}</>
  );
};

export default ResourceScheduleCards;
